import { Suspense, useEffect } from 'react'
import { AppRouter } from './routes/routes'
import { ToastProvider } from './components/Toast'
import { AppThemeProvider } from './theme/AppThemeProvider'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { RealtimeProvider } from './shared/socket/RealtimeProvider'
import { getActiveAcademicYearId } from './shared/api/academicYear'
import { useAuthStore } from './features/auth/hooks/useAuthStore'

function FullPageLoading() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-slate-50 dark:bg-[#0b0f19]">
      <div className="flex flex-col items-center gap-4">
        <div className="relative h-16 w-16">
          <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-800" />
          <div className="absolute inset-0 rounded-full border-4 border-t-indigo-600 dark:border-t-indigo-400 animate-spin" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">School ERP</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 animate-pulse">Initializing application...</p>
      </div>
    </div>
  )
}

function App() {
  useEffect(() => {
    useAuthStore.getState().initializeAuth().catch((err) => {
      console.warn('[App] initializeAuth failed on mount:', err)
    })
    const storedUserRaw = typeof window !== 'undefined' ? localStorage.getItem('school-erp-user') : null
    const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null
    const isOrgAdmin =
      storedUser?.role === 'ORGANIZATIONAL_ADMIN' ||
      storedUser?.userType === 'ORGANIZATIONAL_ADMIN' ||
      localStorage.getItem('school-erp-role') === 'ORGANIZATIONAL_ADMIN'

    // Org admins without a branch context do not use campus academic sessions on mount
    if (!isOrgAdmin) {
      getActiveAcademicYearId().catch((err) => {
        console.warn('[App] Failed to resolve active academic year on mount:', err)
      })
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <ToastProvider>
          <RealtimeProvider>
            <Suspense fallback={<FullPageLoading />}>
              <AppRouter />
            </Suspense>
          </RealtimeProvider>
        </ToastProvider>
      </AppThemeProvider>
    </QueryClientProvider>
  )
}

export default App
